import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'
import { ProjectWithComments, ProjectItem } from '../types/project'
import { useRequests } from '../requests/useRequests'
import { RefreshActions } from '../state/refresh'

export const useProjectItems = (project: ProjectWithComments, onProjectUpdate: (updatedProject: ProjectWithComments) => void) => {
  const dispatch = useDispatch()
  const { id } = useParams()
  const requestMaker = useRequests()
  const [itemModalMode, setItemModalMode] = useState<'add' | 'edit' | null>(null)
  const [itemModalData, setItemModalData] = useState<ProjectItem | Omit<ProjectItem, 'id' | 'position'> | null>(null)

  const openAddItemModal = (stageId?: string) => {
    setItemModalMode('add')
    setItemModalData({
      name: '',
      type: 'product',
      price: 0,
      quantity: 1,
      unit: 'szt.',
      description: '',
      stageId: stageId || project?.stages[0].id,
      url: '',
      status: 'pending',
      internalStatus: 'none',
      imageUrl: '',
      deliveryCost: 0,
      discountPercentage: 0,
      marginPercentage: 0,
      vatPercentage: 23,
      dimensions: '',
      catalogNumber: '',
      supplier: ''
    })
  }

  const openEditModal = (itemId: string) => {
    const item = project?.items.find(item => item.id === itemId)
    if (item != null) {
      setItemModalMode('edit')
      setItemModalData({ ...item })
    }
  }

  const closeItemModal = () => {
    setItemModalMode(null)
    setItemModalData(null)
  }

  const handleSaveItem = async () => {
    if (!id || (itemModalData == null)) return
    if (itemModalMode === 'add') {
      if (project?.stages.length === 0) {
        dispatch(
          RefreshActions.setToastMessage({
            message: 'Najpierw dodaj etap do projektu'
          })
        )
        return
      }
      try {
        await requestMaker.addProjectItem(id, itemModalData as Omit<ProjectItem, 'id' | 'position'>)
        closeItemModal()
        const updatedProject = await requestMaker.getProject(id)
        if (updatedProject) {
          onProjectUpdate(updatedProject.data as ProjectWithComments)
        }
        dispatch(
          RefreshActions.setToastMessage({
            message: 'Element został dodany do projektu'
          })
        )
      } catch (error) {
        dispatch(
          RefreshActions.setToastMessage({
            message: 'Nie udało się dodać elementu'
          })
        )
      }
    } else if (itemModalMode === 'edit') {
      try {
        await requestMaker.updateProjectItem(id, (itemModalData as ProjectItem).id, itemModalData as ProjectItem)
        closeItemModal()
        const updatedProject = await requestMaker.getProject(id)
        if (updatedProject) {
          onProjectUpdate(updatedProject.data as ProjectWithComments)
        }
        dispatch(
          RefreshActions.setToastMessage({
            message: 'Element został zaktualizowany'
          })
        )
      } catch (error) {
        dispatch(
          RefreshActions.setToastMessage({
            message: 'Nie udało się zaktualizować elementu'
          })
        )
      }
    }
  }

  const handleDeleteItem = async (itemId: string): Promise<void> => {
    try {
      const success = await requestMaker.deleteProjectItem(project.id, itemId)
      if (success) {
        const updatedProject = await requestMaker.getProject(project.id)
        if (updatedProject) {
          onProjectUpdate(updatedProject.data as ProjectWithComments)
        }
      }
    } catch (error) {
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się usunąć elementu'
        })
      )
    }
  }

  const handleApproveItem = async (itemId: string): Promise<void> => {
    try {
      const success = await requestMaker.updateProjectItemStatus(project.id, itemId, 'approved')
      if (success) {
        const updatedProject = await requestMaker.getProject(project.id)
        if (updatedProject) {
          onProjectUpdate(updatedProject.data as ProjectWithComments)
        }
      }
    } catch (error) {
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się zatwierdzić elementu'
        })
      )
    }
  }

  const handleRejectItem = async (itemId: string): Promise<void> => {
    try {
      const success = await requestMaker.updateProjectItemStatus(project.id, itemId, 'rejected')
      if (success) {
        const updatedProject = await requestMaker.getProject(project.id)
        if (updatedProject) {
          onProjectUpdate(updatedProject.data as ProjectWithComments)
        }
      }
    } catch (error) {
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się odrzucić elementu'
        })
      )
    }
  }

  const handleChangeInternalItemStatus = async (itemId: string, status: string): Promise<void> => {
    try {
      const success = await requestMaker.updateProjectItemInternalStatus(project.id, itemId, status)
      if (success) {
        const updatedProject = await requestMaker.getProject(project.id)
        if (updatedProject) {
          onProjectUpdate(updatedProject.data as ProjectWithComments)
        }
      }
    } catch (error) {
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się zmienić wewnętrznego statusu elementu'
        })
      )
    }
  }

  const handleCopyItem = async (itemId: string): Promise<void> => {
    try {
      const success = await requestMaker.copyProjectItem(project.id, itemId)
      if (success) {
        const updatedProject = await requestMaker.getProject(project.id)
        if (updatedProject) {
          onProjectUpdate(updatedProject.data as ProjectWithComments)
        }
      }
    } catch (error) {
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się skopiować elementu'
        })
      )
    }
  }

  const handleMoveItemUp = async (itemId: string, stageId: string): Promise<void> => {
    if (!project) return
    const stageItems = project.items
      .filter(item => item.stageId === stageId)
      .sort((a, b) => a.position - b.position)
    const currentIndex = stageItems.findIndex(item => item.id === itemId)
    if (currentIndex <= 0) return

    const updates = [
      {
        id: stageItems[currentIndex].id,
        position: stageItems[currentIndex - 1].position,
        stageId
      },
      {
        id: stageItems[currentIndex - 1].id,
        position: stageItems[currentIndex].position,
        stageId
      }
    ]

    try {
      const success = await requestMaker.reorderProjectItems(project.id, updates)
      if (success) {
        const updatedProject = await requestMaker.getProject(project.id)
        if (updatedProject) {
          onProjectUpdate(updatedProject.data as ProjectWithComments)
        }
      }
    } catch (error) {
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się przenieść elementu'
        })
      )
    }
  }

  const handleMoveItemDown = async (itemId: string, stageId: string): Promise<void> => {
    if (!project) return
    const stageItems = project.items
      .filter(item => item.stageId === stageId)
      .sort((a, b) => a.position - b.position)
    const currentIndex = stageItems.findIndex(item => item.id === itemId)
    if (currentIndex === -1 || currentIndex >= stageItems.length - 1) return

    const updates = [
      {
        id: stageItems[currentIndex].id,
        position: stageItems[currentIndex + 1].position,
        stageId
      },
      {
        id: stageItems[currentIndex + 1].id,
        position: stageItems[currentIndex].position,
        stageId
      }
    ]

    try {
      const success = await requestMaker.reorderProjectItems(project.id, updates)
      if (success) {
        const updatedProject = await requestMaker.getProject(project.id)
        if (updatedProject) {
          onProjectUpdate(updatedProject.data as ProjectWithComments)
        }
      }
    } catch (error) {
      dispatch(
        RefreshActions.setToastMessage({
          message: 'Nie udało się przenieść elementu'
        })
      )
    }
  }

  return {
    itemModalMode,
    itemModalData,
    setItemModalData,
    openAddItemModal,
    openEditModal,
    closeItemModal,
    handleSaveItem,
    handleDeleteItem,
    handleApproveItem,
    handleRejectItem,
    handleChangeInternalItemStatus,
    handleCopyItem,
    handleMoveItemUp,
    handleMoveItemDown
  }
}
